import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar, DollarSign, Clock, Briefcase } from 'lucide-react';

const THEME_PRIMARY = 'rgb(8, 120, 191)';

// Determine current Australian financial year start (Jul 1)
const getDefaultFYStart = () => {
  const now = new Date();
  const year = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return `${year}-07-01`;
};

const toDateStr = (d) => d.toISOString().split('T')[0];

const IncomeAnnualisationCalculator = () => {
  const [scenario, setScenario] = useState(1);
  const [fyStart, setFyStart] = useState(getDefaultFYStart());
  const [employmentStart, setEmploymentStart] = useState('');
  const [payEndDate, setPayEndDate] = useState(toDateStr(new Date()));
  const [ytdIncome, setYtdIncome] = useState('');

  const results = useMemo(() => {
    const income = parseFloat(ytdIncome) || 0;
    if (income <= 0) return null;

    const endDate = new Date(payEndDate);
    let startDate;

    if (scenario === 1) {
      startDate = new Date(fyStart);
    } else {
      if (!employmentStart) return null;
      startDate = new Date(employmentStart);
    }

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;

    const diffMs = endDate.getTime() - startDate.getTime();
    const daysElapsed = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (daysElapsed <= 0) return null;

    const weeksElapsed = daysElapsed / 7;
    const fortnightsElapsed = daysElapsed / 14;

    // Use complete (whole) pay periods for annualisation
    const completeWeeks = Math.floor(daysElapsed / 7);
    const completeFortnights = Math.floor(daysElapsed / 14);

    // Annualised = (YTD / complete_periods) * periods_per_year
    const annualisedWeekly = completeWeeks > 0 ? (income / completeWeeks) * 52 : 0;
    const annualisedFortnightly = completeFortnights > 0 ? (income / completeFortnights) * 26 : 0;

    // Casual equivalent: 48 working weeks out of 52
    const casualWeekly = annualisedWeekly * (48 / 52);
    const casualFortnightly = annualisedFortnightly * (48 / 52);

    // Days progress out of 365
    const daysInYear = 365;
    const progressPct = Math.min((daysElapsed / daysInYear) * 100, 100);

    return {
      daysElapsed,
      weeksElapsed: weeksElapsed.toFixed(2),
      fortnightsElapsed: fortnightsElapsed.toFixed(2),
      annualisedWeekly: Math.round(annualisedWeekly),
      annualisedFortnightly: Math.round(annualisedFortnightly),
      casualWeekly: Math.round(casualWeekly),
      casualFortnightly: Math.round(casualFortnightly),
      progressPct,
      daysRemaining: Math.max(0, daysInYear - daysElapsed),
    };
  }, [scenario, fyStart, employmentStart, payEndDate, ytdIncome]);

  const fmt = (n) =>
    new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);

  return (
    <Card className="border-2 shadow-lg" data-testid="annualisation-calculator">
      <CardHeader className="bg-gradient-to-br from-blue-50 to-white">
        <CardTitle className="text-2xl" style={{ color: THEME_PRIMARY }}>
          Income Annualisation Calculator
        </CardTitle>
        <CardDescription className="text-base">
          Work out your annual income from the money you've received so far this financial year
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-6">
          {/* Scenario Toggle */}
          <div>
            <Label className="text-base font-semibold flex items-center mb-3">
              <Briefcase className="h-4 w-4 mr-2" style={{ color: THEME_PRIMARY }} />
              Employment Scenario
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setScenario(1)}
                className="p-3 rounded-lg border-2 text-left transition-all text-sm"
                style={{
                  borderColor: scenario === 1 ? THEME_PRIMARY : '#e5e7eb',
                  backgroundColor: scenario === 1 ? 'rgb(219, 239, 252)' : 'white',
                }}
                data-testid="scenario-1-btn"
              >
                <span className="font-semibold block" style={{ color: scenario === 1 ? THEME_PRIMARY : '#374151' }}>
                  Scenario 1
                </span>
                <span className="text-gray-500 text-xs">Employed BEFORE start of current financial year</span>
              </button>
              <button
                type="button"
                onClick={() => setScenario(2)}
                className="p-3 rounded-lg border-2 text-left transition-all text-sm"
                style={{
                  borderColor: scenario === 2 ? THEME_PRIMARY : '#e5e7eb',
                  backgroundColor: scenario === 2 ? 'rgb(219, 239, 252)' : 'white',
                }}
                data-testid="scenario-2-btn"
              >
                <span className="font-semibold block" style={{ color: scenario === 2 ? THEME_PRIMARY : '#374151' }}>
                  Scenario 2
                </span>
                <span className="text-gray-500 text-xs">Employed AFTER start of current financial year</span>
              </button>
            </div>
          </div>

          {/* Date inputs */}
          {scenario === 1 ? (
            <div>
              <Label htmlFor="fy-start" className="text-base font-semibold flex items-center mb-2">
                <Calendar className="h-4 w-4 mr-2" style={{ color: THEME_PRIMARY }} />
                Start of Financial Year
              </Label>
              <Input
                id="fy-start"
                type="date"
                value={fyStart}
                onChange={(e) => setFyStart(e.target.value)}
                className="text-lg"
                data-testid="fy-start-input"
              />
            </div>
          ) : (
            <div>
              <Label htmlFor="emp-start" className="text-base font-semibold flex items-center mb-2">
                <Calendar className="h-4 w-4 mr-2" style={{ color: THEME_PRIMARY }} />
                Employment Start Date
              </Label>
              <Input
                id="emp-start"
                type="date"
                value={employmentStart}
                onChange={(e) => setEmploymentStart(e.target.value)}
                className="text-lg"
                data-testid="emp-start-input"
              />
            </div>
          )}

          <div>
            <Label htmlFor="pay-end" className="text-base font-semibold flex items-center mb-2">
              <Calendar className="h-4 w-4 mr-2" style={{ color: THEME_PRIMARY }} />
              Latest Pay Period End Date
            </Label>
            <Input
              id="pay-end"
              type="date"
              value={payEndDate}
              onChange={(e) => setPayEndDate(e.target.value)}
              className="text-lg"
              data-testid="pay-end-input"
            />
          </div>

          {/* YTD Income */}
          <div>
            <Label htmlFor="ytd-income" className="text-base font-semibold flex items-center mb-2">
              <DollarSign className="h-4 w-4 mr-2" style={{ color: THEME_PRIMARY }} />
              Year to Date Income (before tax)
            </Label>
            <Input
              id="ytd-income"
              type="number"
              min="0"
              step="1"
              placeholder="e.g. 45000"
              value={ytdIncome}
              onChange={(e) => setYtdIncome(e.target.value)}
              className="text-lg"
              data-testid="ytd-income-input"
            />
            {ytdIncome && (
              <p className="text-sm text-gray-500 mt-1">{fmt(parseFloat(ytdIncome) || 0)}</p>
            )}
          </div>

          {/* Results */}
          {results && (
            <div className="space-y-4 mt-4" data-testid="annualisation-results">
              {/* Time elapsed bar */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-blue-50 to-white border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="flex items-center text-sm font-semibold text-gray-700">
                    <Clock className="h-4 w-4 mr-1" style={{ color: THEME_PRIMARY }} />
                    Time Elapsed
                  </span>
                  <span className="text-sm text-gray-500">{results.daysElapsed} of 365 days</span>
                </div>
                <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${results.progressPct}%`,
                      background: `linear-gradient(90deg, ${THEME_PRIMARY}, rgb(46, 184, 230))`,
                    }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>{results.weeksElapsed} weeks / {results.fortnightsElapsed} fortnights</span>
                  <span>{results.daysRemaining} days remaining</span>
                </div>
              </div>

              {/* Annualised income results */}
              <div className="p-6 rounded-lg" style={{ backgroundColor: THEME_PRIMARY }}>
                <h4 className="text-white font-semibold text-lg mb-4">
                  {scenario === 1 ? 'Scenario 1' : 'Scenario 2'} &mdash; Annualised Income
                </h4>

                {scenario === 1 ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2 text-sm text-blue-100 pb-2 border-b border-blue-400">
                      <span></span>
                      <span className="text-center font-medium">Weekly Basis</span>
                      <span className="text-center font-medium">Fortnightly Basis</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 items-center">
                      <span className="text-blue-100 text-sm">Annualised Income</span>
                      <span className="text-white text-xl font-bold text-center">{fmt(results.annualisedWeekly)}</span>
                      <span className="text-white text-xl font-bold text-center">{fmt(results.annualisedFortnightly)}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 items-center pt-2 border-t border-blue-400">
                      <span className="text-blue-100 text-sm">Casual Equivalent<br /><span className="text-xs">(48 weeks)</span></span>
                      <span className="text-blue-200 text-lg font-semibold text-center">{fmt(results.casualWeekly)}</span>
                      <span className="text-blue-200 text-lg font-semibold text-center">{fmt(results.casualFortnightly)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center pb-3 border-b border-blue-400">
                      <span className="text-blue-100">Annualised Income</span>
                      <span className="text-white text-2xl font-bold">{fmt(results.annualisedWeekly)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-blue-100">Casual Equivalent (48 weeks)</span>
                      <span className="text-blue-200 text-xl font-semibold">{fmt(results.casualWeekly)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500 text-center mt-4">
            * This calculator provides estimates only. The Australian financial year runs from 1 July to 30 June.
            Contact us for a detailed assessment of your borrowing capacity.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default IncomeAnnualisationCalculator;
